import { z } from 'zod';

import { messagingChannelSchema, type ConnectionStatus, type MessagingChannel } from './enums.js';

/**
 * MESSAGING — dış mesajlaşma kanalları (Slack, WhatsApp, Webhook).
 *
 * Tasarım §4.2'nin `notify_external` aksiyonunun taşıyıcı katmanı. Faz 3.
 * Webhook 10 Eyl 2026'da bağlanabilir oldu; `notify_external`e girmiyor
 * (düz metin değil imzalı JSON taşıyor), yalnız bildirim altyapısının
 * `WEBHOOK` kanalı onu kullanıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `notify` ile farkı: HEDEF BİR ODA, BİR KİŞİ DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * `notify` aksiyonunun hedefi bir KİŞİ (`owners_admins`, `creator`) ve kanal o
 * kişiye nasıl ulaşılacağı — uygulama içi bildirim ya da e-posta. Buradaki
 * hedef bir ODA: bir Slack kanalı ya da bir WhatsApp numarası/grubu.
 * Workspace'in bağladığı tek bir yer ve kim okuduğu bizi ilgilendirmiyor.
 *
 * İki kavramı tek alana sıkıştırmak "`target: 'creator'` Slack'te ne demek"
 * sorusunu cevapsız bırakırdı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 WHATSAPP'TA GÖNDEREN TEK, ALICI KİRACI BAŞINA (18 Ağu 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu alan bir tur "her kiracı kendi numarasını QR ile bağlar" biçimindeydi.
 * Kullanıcı kararıyla değişti ve iki parçaya ayrıldı:
 *
 *   GÖNDEREN → `PlatformMessagingConnection`. TEK kurumsal numara, QR'ı
 *              YALNIZCA platform yöneticisi `/admin/messaging` altında
 *              okutuyor. Kiracının bu numaraya erişimi yok, göremiyor.
 *   ALICI    → `MessagingConnection`. Kiracı panelinde "WhatsApp'ı bağla"
 *              diyen kullanıcı AD SOYAD + TELEFON yazıyor; bildirimler
 *              kurumsal numaradan o telefona gidiyor.
 *
 * Neden: QR akışı bir telefonu ürünün altyapısına bağlıyor ve banlanma riskini
 * o numaranın sahibine yüklüyor. Bunu her müşteriden istemek, riski müşteri
 * sayısıyla çarpmak ve her müşteriden WhatsApp Web'i anlamasını beklemekti.
 * Tek kurumsal numara riski tek yerde topluyor — karşılığında o numara tüm
 * ürünün bildirim yolu ve düştüğünde herkes etkileniyor.
 *
 * ⚠️ Bağlantı hâlâ RESMÎ DEĞİL (Baileys, WhatsApp Web protokolünün tersine
 * mühendislikle yazılmış istemcisi). Meta'nın hizmet şartlarına aykırı, numara
 * banlanabilir ve bu geri alınamaz; teslimat garantisi ve SLA yok. Resmî yol
 * WhatsApp Cloud API (Business hesabı + şablon onayı + mesaj başına ücret).
 * Karar değişirse bu sözleşme aynı kalır, yalnız `WhatsAppSessionManager`
 * değişir.
 *
 * Slack'te böyle bir kırılganlık YOK: incoming webhook resmî, kalıcı ve
 * durumsuz — ve KİRACININ kendi webhook'u, bu ayrımın dışında.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Bağlantı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir kanalın bağlantı durumu — arayüzün gördüğü şey.
 *
 * 🔴 SIR DÖNMEZ. Ne webhook URL'i, ne oturum anahtarı, ne de bunların bir
 * parçası. Kullanıcı Slack webhook'unu yeniden görmek isterse Slack'ten alır;
 * bizim ekranımızda göstermek, o URL'i tarayıcı geçmişine ve ekran
 * görüntülerine yaymak demek — ve o URL tek başına o kanala yazma yetkisi.
 */
export interface MessagingConnectionRow {
  channel: MessagingChannel;
  status: ConnectionStatus;
  /// Slack'te workspace adı; WhatsApp'ta ALICININ AD SOYADI (artık bağlı
  /// numara DEĞİL — bağlı numara kurumsal ve kiracıya gösterilmiyor).
  displayName: string | null;
  /// Kural `destination` vermezse buraya gider. WhatsApp'ta alıcının telefonu.
  defaultDestination: string | null;
  lastError: string | null;
  /// Son BAŞARILI gönderim. `null` ve `status: HEALTHY` ise bağlantı kuruldu
  /// ama hiç kullanılmadı — arayüz "test mesajı gönder" önerebilir.
  lastDeliveredAt: string | null;
  connectedAt: string;
}

export interface MessagingOverview {
  connections: MessagingConnectionRow[];
  /// Bağlanabilir kanallar. `CHANNELS_WITH_TRANSPORT` "taşıyıcı yazıldı mı"
  /// sorusunu cevaplıyor, bu "bu workspace bağlayabilir mi" sorusunu.
  available: MessagingChannel[];
  /// Kurumsal WhatsApp göndericisi ayakta mı.
  ///
  /// 🔴 Kiracı bu bağlantıyı KURAMAZ, yalnız DURUMUNU görür. Göstermemek,
  /// numarasını doğru yazmış bir kullanıcının hiç gelmeyen bildirimleri kendi
  /// hatası sanması demekti — arıza bizde olduğunda ekran bunu söylemeli.
  ///
  /// ⚠️ Numara DÖNMÜYOR. Kurumsal numarayı her kiracıya göstermek, onu bir
  /// destek hattı sanan yanıtların o hatta düşmesi demek.
  sender: {
    whatsappReady: boolean;
  };
}

/**
 * Slack bağlama — incoming webhook URL'i.
 *
 * Neden OAuth değil: OAuth uygulaması Slack'te bir app kaydı, dağıtım onayı ve
 * kapsam listesi ister; kazancı kanal seçimini bizim ekranımızda yapabilmek
 * olurdu. Incoming webhook kullanıcı tarafında iki tık ve tam olarak bir şey
 * yapabiliyor: TEK bir kanala yazmak. Yetki yüzeyi olarak da doğrusu bu —
 * bir bildirim aracının kanal listesini okuması ya da mesaj silmesi gerekmiyor.
 */
export const slackConnectSchema = z.object({
  webhookUrl: z
    .url()
    .refine(
      (v) => v.startsWith('https://hooks.slack.com/'),
      'Slack incoming webhook adresi https://hooks.slack.com/ ile başlamalı',
    ),
  /// Görünen ad; boşsa webhook'un ait olduğu kanal bilinmiyor demektir.
  displayName: z.string().trim().max(120).optional(),
  /// "#alerts" gibi. Slack webhook'u zaten bir kanala bağlı olduğu için bu
  /// alan yalnız GÖSTERİM — gönderimde kullanılmıyor.
  defaultDestination: z.string().trim().max(120).optional(),
});
export type SlackConnectInput = z.infer<typeof slackConnectSchema>;

/**
 * WhatsApp hedefi — telefon numarası ya da grup kimliği.
 *
 * Uluslararası biçimde numara (`905551112233`) ya da grup kimliği
 * (`...@g.us`). Numara biçimi ESNEK doğrulanıyor: boşluk, tire ve `+`
 * temizlendikten sonra 8–15 hane. Katı bir regex, ülke kodu bilmediğimiz bir
 * numarayı reddederdi.
 *
 * Kural `destination` alanı ve alıcı kaydı AYNI doğrulamayı paylaşıyor; iki
 * kopya yazılsaydı biri sıkılaşır, diğeri eski kalırdı.
 */
export const whatsappTargetSchema = z
  .string()
  .trim()
  .min(8)
  .max(120)
  .refine(
    (v) => v.endsWith('@g.us') || /^\d{8,15}$/.test(v.replace(/[\s\-+()]/g, '')),
    'Uluslararası biçimde telefon numarası ya da WhatsApp grup kimliği verin',
  );

/**
 * WHATSAPP ALICI KAYDI — kiracı panelindeki "WhatsApp'ı bağla" formu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 BU FORM BİR OTURUM AÇMIYOR — QR yok, bağlanan bir numara yok
 * ═══════════════════════════════════════════════════════════════════════════
 * Kullanıcı yalnız KİME yazılacağını söylüyor. Gönderen taraf kurumsal numara
 * ve onu `/admin/messaging` bağlıyor (18 Ağu 2026 kararı — gerekçe dosya
 * başlığında).
 *
 * `fullName` neden zorunlu: bu satır bir bildirim hattı ve bir gün "bu numara
 * kimin" diye sorulacak. Yalnız numara tutulsaydı destek masası bir telefon
 * rehberi olmadan bakardı; ayrıca mesajın kendisi de isimle açılabiliyor.
 * Alan `MessagingConnection.displayName`e yazılıyor — Slack'te workspace adının
 * durduğu yer, yani listedeki "ne bağladım" sütunu iki kanalda da dolu.
 */
export const whatsappRecipientSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  phone: whatsappTargetSchema,
});
export type WhatsAppRecipientInput = z.infer<typeof whatsappRecipientSchema>;

/**
 * QR akışının durumu — YALNIZ ADMIN EKRANI okuyor.
 *
 * Arayüz bunu yoklayarak ekranı sürüyor.
 *
 * `qr` yalnız `pending` durumunda dolu ve HER ~20 SANİYEDE DEĞİŞİYOR —
 * WhatsApp QR'ı süreli. Arayüz tek bir kareyi dondurup bırakırsa kullanıcı
 * süresi dolmuş bir kodu okutmaya çalışır ve hiçbir şey olmaz.
 */
export const WHATSAPP_SESSION_STATES = [
  /// Oturum yok, QR henüz üretilmedi.
  'idle',
  /// QR üretildi, kullanıcının okutması bekleniyor.
  'pending',
  /// Telefon eşleşti, WhatsApp sunucularıyla el sıkışılıyor.
  'connecting',
  /// Bağlı ve mesaj gönderilebilir.
  'connected',
  /// Bağlantı düştü; `error` sebebi taşıyor. QR yeniden okutulmalı.
  'failed',
] as const;
export type WhatsAppSessionState = (typeof WHATSAPP_SESSION_STATES)[number];

export interface WhatsAppSessionStatus {
  state: WhatsAppSessionState;
  /// Ham QR dizesi — arayüz kendi QR bileşeniyle çizebilir.
  qr: string | null;
  /// Aynı QR'ın `data:image/png;base64,…` hâli. Arayüz `<img src>` ile basıp
  /// QR kütüphanesi eklemek zorunda kalmasın.
  qrImage: string | null;
  /// Bağlı numara (`connected` durumunda).
  phoneNumber: string | null;
  error: string | null;
}

/**
 * `/admin/messaging/whatsapp` — kurumsal göndericinin tam görünümü.
 *
 * Oturum durumu BELLEKTEN, geri kalanı `PlatformMessagingConnection`
 * satırından geliyor ve ikisi ayrılabiliyor: süreç yeniden başladığında
 * `session.state` `idle` olur ama `connectedAt` doludur — bağlantı kayıtlı,
 * soket henüz açılmamış. Yöneticinin "QR'ı yeniden okutmam gerekiyor mu"
 * sorusunu ayıran tek bilgi bu ayrım; tek bir duruma indirgemek her deploy
 * sonrası yanlış alarm demekti.
 */
export interface PlatformWhatsAppStatus {
  session: WhatsAppSessionStatus;
  /// Kayıt yoksa null — hiç bağlanmamış.
  connection: {
    /// Kayıtlı numara.
    phoneNumber: string | null;
    status: ConnectionStatus;
    lastError: string | null;
    lastDeliveredAt: string | null;
    /// QR'ı okutan yöneticinin adı (yoksa e-postası). Kullanıcı silinmişse
    /// null. Denetim kaydı da bu bilgiyi tutuyor ama `/admin/audit` ekranı
    /// henüz yer tutucu — bugün "bu numarayı kim bağladı" sorusunun tek
    /// görünür cevabı burası.
    connectedBy: string | null;
    connectedAt: string;
  } | null;
}

/// Yöneticinin kurumsal numarayı denemesi — hedefi KENDİSİ yazıyor.
/// Varsayılan bir hedef yok: platform satırının `defaultDestination`ı da yok,
/// çünkü kurumsal numara kimseye "varsayılan olarak" yazmıyor.
export const platformWhatsAppTestSchema = z.object({
  phone: whatsappTargetSchema,
});
export type PlatformWhatsAppTestInput = z.infer<typeof platformWhatsAppTestSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// WEBHOOK — kiracının kendi sistemine imzalı JSON (10 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Giden bildirim webhook'unun imza başlıkları.
 *
 * `X-Mentorivo-Signature: t=<unix>,v1=<hex HMAC-SHA256>`; imzalanan metin
 * `<t>.<ham gövde>`. Burada, sunucunun taşıyıcısında değil, çünkü bağlama
 * diyaloğu alıcıya "şu başlığı doğrula" derken aynı adı göstermeli — iki
 * yerde yazılsaydı biri değişip diğeri eski kalırdı.
 */
export const NOTIFICATION_WEBHOOK_SIGNATURE_HEADER = 'X-Mentorivo-Signature';
export const NOTIFICATION_WEBHOOK_EVENT_HEADER = 'X-Mentorivo-Event';
/// Bağlarken ve "Test" düğmesinde gönderilen olayın tipi. Alıcı bunu
/// tanıyıp ayrı ele alabilsin diye gerçek bildirim tiplerinden ayrı.
export const NOTIFICATION_WEBHOOK_TEST_EVENT = 'system.webhook_test';

/**
 * WEBHOOK BAĞLAMA — kiracının bildirimleri kendi sistemine alması.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İMZA SIRRINI SUNUCU ÜRETİYOR ve YALNIZ BİR KEZ gösteriyor
 * ═══════════════════════════════════════════════════════════════════════════
 * 32 rastgele bayt, `MessagingConnection.credentialEnc`e `TOKEN_VAULT_KEY`
 * ile şifreli yazılıyor ve yalnız bağlama yanıtında (`WebhookConnectResult.
 * secret`) dönüyor. `GET /messaging` onu hiç okumuyor. Kaybeden kullanıcı
 * yeniden bağlıyor ve yeni bir sır alıyor — eski sır o anda ölüyor.
 *
 * Neden kullanıcıya yazdırılmıyor: elle seçilen sır "şirketadi2026" olur ve
 * imzanın bütün değeri tahmin edilemezliğinde.
 *
 * ---------------------------------------------------------------------------
 * Kaydetmeden önce TEST — Slack'le aynı karar
 * ---------------------------------------------------------------------------
 * Adres, kaydedilmeden önce imzalı bir `system.webhook_test` olayıyla
 * deneniyor ve 2xx dönmezse HİÇBİR ŞEY kaydedilmiyor. ⚠️ Sonucu: alıcı ilk
 * bağlantıda imzayı doğrulayamaz (sırrı henüz bilmiyor). Diyalog bunu
 * söylüyor — önce kabul et, sırrı al, SONRA doğrulamayı aç.
 */
export const webhookConnectSchema = z.object({
  url: z
    .url()
    .max(2000)
    .refine((v) => /^https:\/\//i.test(v), 'Webhook adresi https:// ile başlamalı'),
  /// Listede görünen ad; boşsa adresin ana adı gösteriliyor.
  displayName: z.string().trim().max(120).optional(),
});
export type WebhookConnectInput = z.infer<typeof webhookConnectSchema>;

export interface WebhookConnectResult {
  ok: boolean;
  /// Başarısızsa alıcının cevabı ya da ağ hatası (sır alanları yok).
  error: string | null;
  /// Test isteğine alıcının döndürdüğü HTTP durumu; ağ hatasında `null`.
  status: number | null;
  /// 🔴 İmza sırrı — YALNIZ bu yanıtta ve yalnız `ok: true` iken dolu.
  secret: string | null;
}

/// Bağlantı kurulduktan sonra "gerçekten gidiyor mu" sorusunun cevabı.
/// Kullanıcı bunu görmeden kural kurarsa, ilk gerçek bildirimde öğrenir.
export const messagingTestSchema = z.object({
  channel: messagingChannelSchema,
  /// Boşsa workspace varsayılanı.
  destination: z.string().trim().max(120).optional(),
});
export type MessagingTestInput = z.infer<typeof messagingTestSchema>;

export interface MessagingTestResult {
  ok: boolean;
  /// Başarısızsa ham sağlayıcı hatası (sır alanları temizlenmiş).
  error: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Gönderim — alanlar arası sözleşme
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `MessagingService.send(input)` — `notify_external` aksiyonunun çağırdığı
 * tek metot.
 *
 * 🔴 FIRLATMIYOR, `{ok}` DÖNÜYOR. Gerekçe `PlatformActionPort` ile aynı
 * (Faz 0, `analytics/platform-action.adapter.ts`): bir kural iki kanala birden
 * yazıyorsa Slack'in reddi WhatsApp'ı düşürmemeli, ve bildirim gönderilemedi
 * diye UYGULANMIŞ bir bütçe değişikliği "başarısız" sayılmamalı.
 */
export interface MessagingSendInput {
  workspaceId: string;
  channel: MessagingChannel;
  /// Boşsa `MessagingConnection.defaultDestination`.
  destination?: string | null;
  /// Düz metin. Biçimlendirme YOK ve bu bilinçli: Slack `mrkdwn`, WhatsApp
  /// kendi işaretlemesini kullanıyor ve ikisi aynı değil. Tek gövdeyi iki
  /// sözdizimine göre kaçırmak, birinde `*kalın*` diğerinde yıldız görünmesi
  /// demekti.
  text: string;
}

export interface MessagingSendResult {
  ok: boolean;
  /// `ok: false` olduğunda makine tarafından okunabilir sebep.
  code: MessagingErrorCode | null;
  error: string | null;
}

export const MessagingErrorCode = {
  /// Bu workspace o kanalı hiç bağlamamış.
  MESSAGING_NOT_CONNECTED: 'MESSAGING_NOT_CONNECTED',
  /// Kurumsal WhatsApp numarası bağlı değil — arıza KİRACIDA DEĞİL, bizde.
  /// `MESSAGING_NOT_CONNECTED`ten ayrı tutuluyor çünkü kullanıcıya söylenecek
  /// şey farklı: birincisinde "numaranı gir", ikincisinde "yapacağın bir şey
  /// yok". İkisini tek koda indirgemek, doğru yapmış kullanıcıya kendi
  /// hatasıymış gibi bir cümle göstermek olurdu.
  MESSAGING_SENDER_OFFLINE: 'MESSAGING_SENDER_OFFLINE',
  /// Bağlantı var ama koptu (WhatsApp oturumu düştü, webhook iptal edildi).
  MESSAGING_DISCONNECTED: 'MESSAGING_DISCONNECTED',
  /// Hedef oda ne kuralda ne de varsayılanda verilmiş.
  MESSAGING_NO_DESTINATION: 'MESSAGING_NO_DESTINATION',
  /// Sağlayıcı reddetti (geçersiz webhook, banlanmış numara, biçim hatası).
  MESSAGING_SEND_FAILED: 'MESSAGING_SEND_FAILED',
  /// QR akışı sürerken ikinci bir bağlanma isteği geldi.
  MESSAGING_SESSION_BUSY: 'MESSAGING_SESSION_BUSY',
  MESSAGING_CONNECTION_EXISTS: 'MESSAGING_CONNECTION_EXISTS',
  /// Webhook adresi kabul edilmedi: HTTPS değil, içinde kimlik bilgisi var ya
  /// da iç ağa gidiyor (`localhost`, `10.x`, bulut üstveri adresi…). Biçim
  /// hatası zod'da yakalanıyor; bu kod "biçim doğru ama buraya istek atmayız".
  MESSAGING_WEBHOOK_URL_REJECTED: 'MESSAGING_WEBHOOK_URL_REJECTED',
} as const;
export type MessagingErrorCode = (typeof MessagingErrorCode)[keyof typeof MessagingErrorCode];

/**
 * Mesaj gövdesi sınırı.
 *
 * Slack 40.000 karaktere kadar kabul ediyor, WhatsApp 4.096. Ortak sınır
 * küçüğü ama 1.000'de kesiyoruz: bunlar BİLDİRİM, rapor değil. Telefon
 * ekranında okunmayan bir mesaj gönderilmemiş sayılır ve kural yazarı
 * `message` alanına roman yazdığında bunu erken öğrenmeli.
 *
 * Kesme sessiz DEĞİL — sonuna `…` konuyor.
 */
export const MESSAGING_MAX_TEXT_LENGTH = 1000;
